import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Lock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/efs/PageHeader';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { KgValue } from '@/components/efs/KgValue';
import { formatDate, formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  useLockMaterialValuation,
  useMaterialValuation,
  useRecomputeMaterialValuation,
} from './useMonthlyMaterialValuation';
import type {
  MaterialValuationDto,
  MaterialValuationLineDto,
  MaterialValuationStatus,
} from './types';

const NS = 'valuation';
const P = 'efs.valuation.monthly-material';

const statusVariant: Record<MaterialValuationStatus, 'secondary' | 'default' | 'outline'> = {
  open: 'secondary',
  calculated: 'default',
  locked: 'outline',
};

/** Format a money decimal string (no kg unit) with thousands separators. */
const formatMoney = (value: string | null | undefined): string => {
  if (value == null || value === '') return '-';
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

const isNegative = (value: string | null | undefined): boolean => {
  if (value == null || value === '') return false;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n < 0;
};

export const MonthlyMaterialValuationPage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();

  const canRecompute = hasPermission(user, 'valuation.market_price.apply');
  const canLock = hasPermission(user, 'valuation.confirm');

  const [lockOpen, setLockOpen] = useState(false);

  const query = useMaterialValuation(batchId);
  const recompute = useRecomputeMaterialValuation(batchId);
  const lock = useLockMaterialValuation(batchId);

  const valuation = query.data ?? null;
  const lines = valuation?.lines ?? [];
  const unpricedNames = lines
    .filter((l) => !l.price_reference_id)
    .map((l) => l.material_name ?? l.material_id);
  const isLocked = valuation?.status === 'locked';

  // Surface the most recent action error (e.g. backend Conflict on recompute/lock).
  const actionError = recompute.error ?? lock.error ?? null;

  const onRecompute = () => {
    recompute.mutate();
  };

  const onConfirmLock = async () => {
    if (!valuation) return;
    await lock.mutateAsync(valuation.monthly_material_valuation_id);
    setLockOpen(false);
  };

  const columns = useMemo<MasterTableColumn<MaterialValuationLineDto>[]>(
    () => [
      {
        key: 'material_id',
        header: t(`${P}.fields.material`),
        cell: (row) =>
          row.material_name ? (
            <span>{row.material_name}</span>
          ) : (
            <span className="font-mono text-xs">{row.material_id}</span>
          ),
      },
      {
        key: 'weight',
        header: t(`${P}.fields.weight`),
        align: 'right',
        cell: (row) => <KgValue value={row.weight} />,
      },
      {
        key: 'unit_price',
        header: t(`${P}.fields.unit_price`),
        align: 'right',
        cell: (row) =>
          row.price_reference_id ? (
            <span className="font-mono tabular-nums">{formatMoney(row.unit_price)}</span>
          ) : (
            <Badge variant="destructive">{t(`${P}.messages.no_price_badge`)}</Badge>
          ),
      },
      {
        key: 'line_value',
        header: t(`${P}.fields.line_value`),
        align: 'right',
        cell: (row) => (
          <span className="font-mono tabular-nums">{formatMoney(row.line_value)}</span>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(`${P}.title`)}
        subtitle={t(`${P}.subtitle`)}
        actions={
          batchId ? (
            <>
              {canRecompute ? (
                <Button
                  variant="outline"
                  onClick={onRecompute}
                  disabled={recompute.isPending || isLocked}
                >
                  {recompute.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-1 h-4 w-4" />
                  )}
                  {t(`${P}.actions.recompute`)}
                </Button>
              ) : null}
              {canLock ? (
                <Button
                  onClick={() => setLockOpen(true)}
                  disabled={lock.isPending || !valuation || valuation.status !== 'calculated'}
                >
                  <Lock className="mr-1 h-4 w-4" />
                  {t(`${P}.actions.lock`)}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />

      {actionError ? (
        <Alert variant="destructive">
          <AlertTitle>{t(`${P}.error.title`)}</AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-efs-text-muted">{t(`${P}.messages.loading`)}</p>
      ) : !valuation ? (
        <Alert>
          <AlertDescription>{t(`${P}.messages.empty`)}</AlertDescription>
        </Alert>
      ) : (
        <>
          <SummaryCard valuation={valuation} />
          {unpricedNames.length > 0 ? (
            // Calculation is unchanged (unpriced lines count as 0); this only
            // makes the zero visible before someone locks value A.
            <Alert variant="destructive">
              <AlertTitle>{t(`${P}.messages.no_price_title`)}</AlertTitle>
              <AlertDescription>
                {t(`${P}.messages.no_price_description`, {
                  materials: unpricedNames.join('、'),
                })}
              </AlertDescription>
            </Alert>
          ) : null}
          <MasterTable
            columns={columns}
            rows={lines}
            rowKey={(row) => row.monthly_material_valuation_line_id}
            isLoading={query.isLoading}
            emptyMessage={t(`${P}.messages.no_lines`)}
          />
        </>
      )}

      <Dialog open={lockOpen} onOpenChange={setLockOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(`${P}.lock_confirm.title`)}</DialogTitle>
            <DialogDescription>{t(`${P}.lock_confirm.description`)}</DialogDescription>
          </DialogHeader>
          {lock.error ? (
            <Alert variant="destructive">
              <AlertDescription>{lock.error.message}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockOpen(false)} disabled={lock.isPending}>
              {t(`${P}.actions.cancel`)}
            </Button>
            <Button onClick={onConfirmLock} disabled={lock.isPending}>
              {lock.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t(`${P}.actions.lock`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryCard = ({ valuation }: { valuation: MaterialValuationDto }) => {
  const { t } = useTranslation(NS);
  const hasFirst = valuation.first_valuation_total != null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base">{t(`${P}.summary.title`)}</CardTitle>
        <Badge variant={statusVariant[valuation.status]}>
          {t(`${P}.status.${valuation.status}`, { defaultValue: valuation.status })}
        </Badge>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <p className="text-xs text-efs-text-muted">{t(`${P}.summary.total_value_a`)}</p>
          <p className="font-mono text-lg font-semibold tabular-nums">
            {formatMoney(valuation.total_value_a)}
          </p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">{t(`${P}.summary.first_valuation_total`)}</p>
          <p className="font-mono text-lg tabular-nums">
            {hasFirst ? formatMoney(valuation.first_valuation_total) : t(`${P}.summary.not_locked`)}
          </p>
        </div>
        <div>
          <p className="text-xs text-efs-text-muted">{t(`${P}.summary.difference_value`)}</p>
          <p
            className={`font-mono text-lg font-semibold tabular-nums ${
              isNegative(valuation.difference_value) ? 'efs-text-critical' : ''
            }`}
          >
            {hasFirst ? formatMoney(valuation.difference_value) : '-'}
          </p>
        </div>
        <div className="sm:col-span-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-efs-border pt-3 text-xs text-efs-text-muted">
          <span>
            {t(`${P}.fields.price_basis_date`)}: {formatDate(valuation.price_basis_date)}
          </span>
          {valuation.locked_at ? (
            <span>
              {t(`${P}.fields.locked_at`)}: {formatDateTime(valuation.locked_at)}
            </span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default MonthlyMaterialValuationPage;
